/**
 * OrderInfoPanel — card dati ordine per la colonna destra di MailPage.
 *
 * Non esiste un endpoint singolo-ordine dedicato (grep in features/ordini/api.ts:
 * solo `useOrders` con lista + filtro `q`). Riusiamo lo stesso endpoint
 * `/api/orders?q=...` e prendiamo il match esatto sulla reference.
 * Fetch best-effort: errore non-bloccante (thread + allegati restano
 * consultabili anche se l'ordine non è (più) in Poleepo).
 */
import { useEffect, useState } from "react";
import { apiGet, prettifyApiError } from "@/shared/api";
import { MarketplaceLogo } from "@/shared/ui/MarketplaceLogo";
import { orderMarketplaceLogo } from "@/features/ordini/components/OrderCard";

interface ApiOrderItem {
  reference: string;
  customer_name: string;
  channel: string;
  status: string;
  packages_count: number;
  supplier_order_ref: string;
  total_amount: string | number;
  marketplace?: string | null;
  raw?: { source?: string; tracking_code?: string; tracking_number?: string } | null;
}

function trackingCode(order: ApiOrderItem): string | null {
  const code = order.raw?.tracking_code ?? order.raw?.tracking_number;
  return code && code.trim() ? code.trim() : null;
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="min-w-0">
      <div className="text-[12px] text-ink-2">{label}</div>
      <div className="truncate text-[13px] text-ink">{value}</div>
    </div>
  );
}

function SkeletonRow() {
  return (
    <div className="min-w-0">
      <div className="h-2.5 w-16 rounded bg-paper-2 mb-1.5" />
      <div className="h-3 w-24 rounded bg-paper-2" />
    </div>
  );
}

export function OrderInfoPanel({ orderRef }: { orderRef: string }) {
  const [order, setOrder] = useState<ApiOrderItem | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const ctrl = new AbortController();
    setIsLoading(true);
    setError(null);
    apiGet<{ items: ApiOrderItem[] }>(`/api/orders?q=${encodeURIComponent(orderRef)}&limit=10`, {
      signal: ctrl.signal,
    })
      .then((res) => {
        const match = res.items.find((it) => it.reference === orderRef) ?? null;
        setOrder(match);
        if (!match) setError("Ordine non trovato in Poleepo.");
      })
      .catch((err: unknown) => {
        if (ctrl.signal.aborted) return;
        setError(prettifyApiError(err));
      })
      .finally(() => {
        if (!ctrl.signal.aborted) setIsLoading(false);
      });
    return () => ctrl.abort();
  }, [orderRef]);

  return (
    <div className="rounded-xl border border-line p-4">
      <h2 className="mb-3 font-display text-[14px] font-bold text-ink">Dati ordine</h2>

      {isLoading && (
        <div className="grid grid-cols-2 gap-x-4 gap-y-2.5" data-testid="order-info-skeleton">
          {Array.from({ length: 6 }).map((_, i) => (
            <SkeletonRow key={i} />
          ))}
        </div>
      )}

      {!isLoading && error && <p className="text-[12.5px] text-ink-2">{error}</p>}

      {!isLoading && !error && order && (
        <div className="grid grid-cols-2 gap-x-4 gap-y-2.5">
          <Row label="Cliente" value={order.customer_name || "—"} />
          <Row
            label="Marketplace"
            value={<MarketplaceLogo {...orderMarketplaceLogo(order)} size={14} showLabel />}
          />
          <Row label="Stato" value={order.status || "—"} />
          <Row label="Colli" value={order.packages_count || "—"} />
          <Row label="Tracking" value={trackingCode(order) ?? "—"} />
          <Row label="N.Ord.Forn." value={order.supplier_order_ref || "—"} />
          <Row
            label="Totale"
            value={
              order.total_amount
                ? `${Number(order.total_amount).toLocaleString("it-IT", { minimumFractionDigits: 2 })} €`
                : "—"
            }
          />
        </div>
      )}
    </div>
  );
}
